/**
 * -----------------------------------------------------------------------------
 * Shopify Cart Promotion Engine (Accurate Auto-Remove & Clean Empty State)
 * - Removes free gift 100% reliably via /cart/change.js when spend < 999.-
 * - Removes free gift immediately if user deletes all normal products
 * - Prevents user from being charged for orphaned free gifts
 * - Reloads /cart page when gift is removed so empty state & totals refresh
 * -----------------------------------------------------------------------------
 */

if (typeof window.CartPromotionEngineLoaded === 'undefined') {
  window.CartPromotionEngineLoaded = true;

  (function() {
    class CartPromotionEngine {
  constructor() {
    this.config = null;
    this.isUpdating = false;
    this._timer = null;
    this._fetchDebounce = null;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    const configEl = document.getElementById('cart-promotion-config');
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    this.cacheDOM();
    if (sessionStorage.getItem('cpe_promo_dismissed') === 'true') {
      if (this.container) this.container.style.display = 'none';
    }
    this.bindEvents();
    this.interceptNetworkRequests();
    this.fetchAndEvaluate();
  }

  cacheDOM() {
    this.container = document.getElementById('cart-promotion-engine');
    if (!this.container) return;

    this.barFill = this.container.querySelector('.cart-promo-bar-fill');
    this.currentSpendLabel = this.container.querySelector('[data-current-spend-label]');
    this.thresholdSpendLabel = this.container.querySelector('.cart-promo-threshold-spend');
    this.promoMessage = this.container.querySelector('[data-promo-message]');
    this.headlineEl = this.container.querySelector('.cart-promo-headline');
    this.iconEl = this.container.querySelector('.cart-promo-icon');
    this.valueLabel = this.container.querySelector('[data-gift-value-label]');
    this.excludedNoteEl = this.container.querySelector('[data-promo-excluded-note]');
  }

  isEnglish() {
    if (this.config && this.config.isEnglish === true) return true;
    const htmlLang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    if (htmlLang === 'en') return true;
    const path = window.location.pathname.toLowerCase();
    if (path.startsWith('/en') || path.includes('/en/')) return true;
    return false;
  }

  /**
   * ดักจับคำสั่ง AJAX ตะกร้าของทุกระบบ (ธีม, แอป, หน้า Cart, Drawer)
   */
  interceptNetworkRequests() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');

      if (
        url.includes('/cart/change') ||
        url.includes('/cart/add') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear')
      ) {
        if (!self.isUpdating) {
          clearTimeout(self._fetchDebounce);
          self._fetchDebounce = setTimeout(() => {
            self.fetchAndEvaluate();
          }, 200);
        }
      }

      return response;
    };
  }

  bindEvents() {
    // 1. เชื่อมต่อระบบ FoxTheme PubSub โดยตรง
    if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.PUB_SUB_EVENTS) {
      window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
        if (!this.isUpdating) {
          if (data && data.cart) this.evaluateState(data.cart);
          else this.fetchAndEvaluate();
        }
      });
    }

    // 2. ดักฟัง Event ตะกร้ามาตรฐาน
    document.addEventListener('cart:updated', (e) => {
      if (!this.isUpdating) {
        if (e.detail && e.detail.cart) this.evaluateState(e.detail.cart);
        else this.fetchAndEvaluate();
      }
    });

    document.addEventListener('cart:refresh', () => {
      if (!this.isUpdating) this.fetchAndEvaluate();
    });

    document.addEventListener('shopify:section:load', () => {
      this.cacheDOM();
      this.fetchAndEvaluate();
    });

    // 3. ดักฟังการคลิกปุ่มต่างๆ ในหน้า Cart & Drawer
    document.addEventListener('click', (e) => {
      // 0. ปุ่มกากบาทปิดการ์ดโปรโมชั่น (Close / Dismiss)
      const closeBtn = e.target.closest('#cart-promo-close-btn, .cart-promo-close-btn');
      if (closeBtn) {
        e.preventDefault();
        sessionStorage.setItem('cpe_promo_dismissed', 'true');
        if (this.container) {
          this.container.style.transition = 'all 0.3s ease';
          this.container.style.opacity = '0';
          this.container.style.transform = 'translateY(-10px)';
          setTimeout(() => {
            if (this.container) this.container.style.display = 'none';
          }, 300);
        }
        return;
      }

      // ปุ่มเลือกสินค้าเพิ่ม (ลิงก์ตรงไปหน้า /collections/all)
      const shopMoreBtn = e.target.closest('#cart-promo-shop-more-btn, #cart-promo-upsell-toggle, .cart-promo-shop-more-btn, .cart-promo-upsell-trigger-btn');
      if (shopMoreBtn) {
        e.preventDefault();
        window.location.href = '/collections/all';
        return;
      }

      // ปุ่มกดรับของแถม (หลังจากที่เคยลบออกไป)
      const claimBtn = e.target.closest('#cart-promo-claim-btn, .cart-promo-claim-btn');
      if (claimBtn) {
        e.preventDefault();
        sessionStorage.removeItem('cpe_gift_opt_out');
        this.addSingleGift();
        return;
      }

      // ตรวจจับเมื่อลูกค้ากดปุ่ม "ลบ" ของแถม
      const removeBtn = e.target.closest('.cart-item__remove, [data-cart-remove], .cart-remove-button, [is="cart-remove-item"], a[href*="/cart/change"]');
      if (removeBtn) {
        const itemRow = removeBtn.closest('.cart-item, [id^="CartDrawer-Item-"], [id^="CartItem-"], tr, .drawer__item');
        if (itemRow) {
          const rowText = itemRow.textContent || '';
          const isGiftRow = rowText.includes('ของแถม') || rowText.includes('Value Set');
          if (isGiftRow) {
            sessionStorage.setItem('cpe_gift_opt_out', 'true');
          }
        }
      }

      // เมื่อคลิกปุ่มปรับจำนวนสินค้า (+ / -)
      const qtyBtn = e.target.closest('button[name="plus"], button[name="minus"], .quantity__button, [data-quantity-selector]');
      if (qtyBtn) {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this.fetchAndEvaluate(), 250);
      }
    });

    // ตรวจจับเมื่อมีการพิมพ์ตัวเลขจำนวนสินค้าโดยตรง
    document.addEventListener('change', (e) => {
      if (e.target && e.target.matches('input[name="updates[]"], .quantity__input')) {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this.fetchAndEvaluate(), 250);
      }
    });
  }

  async fetchAndEvaluate() {
    if (this.isUpdating) return;
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!res.ok) return;
      const cart = await res.json();
      this.evaluateState(cart);
    } catch (e) {}
  }

  /**
   * คำนวณและประเมินยอดเงินตามความจริง
   */
  async evaluateState(cart) {
    this.cacheDOM();
    if (!this.config || !this.container) return;

    let eligibleSubtotal = 0;
    const giftItems = [];
    let hasExcludedItems = false;

    // วนตรวจจับสินค้าในตะกร้าอย่างละเอียด
    if (cart.items && cart.items.length > 0) {
      for (const item of cart.items) {
        let isGift = false;

        if (item.properties && (item.properties._promotion_gift === 'true' || item.properties._promotion_gift === true)) {
          isGift = true;
        } else if (this.config.giftVariantId && String(item.variant_id) === String(this.config.giftVariantId)) {
          isGift = true;
        } else if (item.title && (item.title.includes('ของแถม') || item.title.includes('Value Set'))) {
          isGift = true;
        }

        if (isGift) {
          giftItems.push(item);
          continue;
        }

        // ตรวจสอบสินค้า 1 แถม 1 ที่ไม่ร่วมรายการสะสมยอด (Dreamy Glow Cushion, Make Me Matte, tag__sale_กด 2 ชิ้น 1 แถม 1)
        let isExcluded = false;
        const titleLower = (item.title || '').toLowerCase();
        const handleLower = (item.handle || '').toLowerCase();

        if (
          titleLower.includes('dreamy glow') ||
          titleLower.includes('make me matte') ||
          titleLower.includes('1 แถม 1') ||
          titleLower.includes('1แถม1') ||
          handleLower.includes('dreamy-glow') ||
          handleLower.includes('make-me-matte')
        ) {
          isExcluded = true;
        }

        // ตรวจจับจาก discounts ของ item (ถ้ามีส่วนลด 1 แถม 1)
        if (item.discounts && item.discounts.length > 0) {
          for (const d of item.discounts) {
            const dTitle = (d.title || '').toLowerCase();
            if (dTitle.includes('1 แถม 1') || dTitle.includes('1แถม1') || dTitle.includes('bogo')) {
              isExcluded = true;
            }
          }
        }

        if (isExcluded) {
          hasExcludedItems = true;
          // สินค้า 1 แถม 1 จะไม่ถูกนำมารวมคำนวณสะสมยอด 999.- เด็ดขาด!
          continue;
        }

        // คิดเงินเฉพาะสินค้าปกติที่ร่วมรายการเท่านั้น (ไม่รวมของแถม และไม่รวม 1 แถม 1)
        eligibleSubtotal += Number(item.final_line_price !== undefined ? item.final_line_price : item.line_price || 0);
      }
    }

    const threshold = Number(this.config.threshold || 99900);
    const isEligible = eligibleSubtotal >= threshold;
    const remainingCents = Math.max(0, threshold - eligibleSubtotal);
    const progressPercent = threshold > 0 ? Math.min(100, Math.round((eligibleSubtotal / threshold) * 100)) : 100;

    const totalGiftQuantity = giftItems.reduce((sum, item) => sum + item.quantity, 0);
    const hasOptedOut = sessionStorage.getItem('cpe_gift_opt_out') === 'true';

    // กรณีที่ 1: ยอดไม่ถึงเป้า 999.- (หรือลบสินค้าปกติออกหมดจนเหลือแต่ของแถม) -> ลบของแถมทั้งหมดออกทันที!
    if (!isEligible) {
      sessionStorage.removeItem('cpe_gift_opt_out');
      if (totalGiftQuantity > 0) {
        await this.removeAllGifts(giftItems);
        return;
      }
    }

    // กรณีที่ 2: ยอดถึงเป้า และยังไม่มีของแถม
    if (isEligible && totalGiftQuantity === 0) {
      if (!hasOptedOut) {
        await this.addSingleGift();
        return;
      }
    }

    // กรณีที่ 3: มีของแถมเกิน 1 ชิ้น -> ปรับลดให้เหลือ 1 ชิ้น
    if (isEligible && totalGiftQuantity > 1) {
      await this.normalizeGiftToSingle(giftItems);
      return;
    }

    // อัปเดตการ์ดโปรโมชั่น Real-Time
    this.updateUI({
      eligibleSubtotal,
      threshold,
      remainingCents,
      progressPercent,
      isCompleted: isEligible,
      giftInCart: totalGiftQuantity > 0,
      hasOptedOut: hasOptedOut && totalGiftQuantity === 0,
      hasExcludedItems
    });
  }

  /**
   * เพิ่มของแถม 1 ชิ้น และสั่งรีเฟรชหน้าจอ
   */
  async addSingleGift() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: this.config.giftVariantId,
          quantity: 1,
          properties: { '_promotion_gift': 'true', '_locked_qty': '1' }
        })
      });

      if (response.ok) {
        await this.triggerThemeRefresh();
      }
    } catch (e) {
      console.error('[CartPromotionEngine] Add gift error:', e);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * ลบของแถมออกทั้งหมดอย่างแน่นอน 100% ผ่าน /cart/change.js
   */
  async removeAllGifts(giftItems) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      // ใช้ /cart/change.js ลบทีละชิ้น เพื่อความแน่นอนสูงสุดในทุก Store
      for (const item of giftItems) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: item.key || item.variant_id,
            quantity: 0
          })
        });
      }

      await this.triggerThemeRefresh();
    } catch (e) {
      console.error('[CartPromotionEngine] Remove gifts error:', e);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * ปรับลดของแถมให้เหลือชิ้นเดียว
   */
  async normalizeGiftToSingle(giftItems) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      for (let i = 0; i < giftItems.length; i++) {
        const item = giftItems[i];
        const targetQty = i === 0 ? 1 : 0;
        if (item.quantity !== targetQty) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              id: item.key || item.variant_id,
              quantity: targetQty
            })
          });
        }
      }

      await this.triggerThemeRefresh();
    } catch (e) {
      console.error('[CartPromotionEngine] Normalize gift error:', e);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * รีเฟรชทั้ง Drawer และหน้า Cart Page เต็มจอ
   */
  async triggerThemeRefresh() {
    const isCartPage = window.location.pathname.endsWith('/cart') || Boolean(document.querySelector('main-cart, .cart__items, #MainCart-items'));
    
    // ถ้าอยู่บนหน้า Cart Page (/cart) ให้ reload ทันทีเพื่อให้ทั้งตาราง ยอดรวม และหน้าตะกร้าว่างอัปเดต 100%
    if (isCartPage) {
      window.location.reload();
      return;
    }

    // ถ้าอยู่ใน CartDrawer สั่งให้ธีมรีเฟรช Drawer
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  }

  updateUI(state) {
    const { eligibleSubtotal, threshold, remainingCents, progressPercent, isCompleted, giftInCart, hasOptedOut } = state;
    this.cacheDOM();
    if (!this.container) return;

    if (sessionStorage.getItem('cpe_promo_dismissed') === 'true') {
      this.container.style.display = 'none';
      return;
    }

    const isEn = this.isEnglish();

    if (isCompleted) this.container.classList.add('is-completed');
    else this.container.classList.remove('is-completed');

    if (this.barFill) this.barFill.style.width = `${progressPercent}%`;
    if (this.currentSpendLabel) this.currentSpendLabel.textContent = this.formatMoney(eligibleSubtotal);
    if (this.thresholdSpendLabel) this.thresholdSpendLabel.textContent = this.formatMoney(threshold);

    if (this.valueLabel) {
      const valText = this.config.giftValue || '฿1,050';
      this.valueLabel.textContent = isEn ? `Value ${valText}` : `มูลค่า ${valText}`;
    }

    if (this.promoMessage) {
      if (isCompleted) {
        if (giftInCart) {
          this.promoMessage.innerHTML = isEn
            ? `<span class="cart-promo-success-text"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg> Free gift has been added to your cart</span>`
            : `<span class="cart-promo-success-text"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg> เพิ่มของขวัญลงตะกร้าเรียบร้อยแล้ว</span>`;
        } else if (hasOptedOut) {
          this.promoMessage.innerHTML = isEn
            ? `<span class="cart-promo-optout-wrap">You removed the free gift <button type="button" class="cart-promo-claim-btn" id="cart-promo-claim-btn">+ Claim Free Gift</button></span>`
            : `<span class="cart-promo-optout-wrap">คุณนำของขวัญออกจากตะกร้าแล้ว <button type="button" class="cart-promo-claim-btn" id="cart-promo-claim-btn">+ กดรับของขวัญฟรี</button></span>`;
        }
      } else {
        this.promoMessage.innerHTML = isEn
          ? `<span class="cart-promo-remaining-text">Add <strong data-remaining-amount>${this.formatMoney(remainingCents)}</strong> more to get a free gift</span>`
          : `<span class="cart-promo-remaining-text">ซื้อเพิ่มอีก <strong data-remaining-amount>${this.formatMoney(remainingCents)}</strong> เพื่อรับของฟรี</span>`;
      }
    }

    if (this.headlineEl) {
      if (isCompleted) {
        this.headlineEl.textContent = isEn ? 'Congratulations! You unlocked a FREE gift' : 'ยินดีด้วย! คุณได้รับของขวัญแล้ว';
      } else {
        this.headlineEl.textContent = isEn ? `Spend ${this.formatMoney(threshold)} to get a FREE gift` : `ช้อปครบ ${this.formatMoney(threshold)} รับฟรี`;
      }
    }

    if (this.iconEl) {
      this.iconEl.innerHTML = isCompleted
        ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`
        : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="14" rx="2"></rect><path d="M12 8v14"></path><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"></path></svg>`;
    }

    if (this.excludedNoteEl) {
      if (state.hasExcludedItems) {
        this.excludedNoteEl.style.display = 'block';
        this.excludedNoteEl.textContent = isEn
          ? '*1 Get 1 Free items are excluded from the gift threshold'
          : '*(สินค้าโปรโมชั่น 1 แถม 1 ไม่ร่วมรายการสะสมยอดของแถม)';
      } else {
        this.excludedNoteEl.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') cents = 0;
    const isEn = this.isEnglish();
    const amount = (cents / 100).toLocaleString(isEn ? 'en-US' : 'th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${amount} ฿`;
  }
  }

  window.CartPromotionEngine = new CartPromotionEngine();
})();
}
